/**
 * Utilities for handling card templates and choices.
 *
 * Imports utility functions and models from external modules.
 */
import { log, defined, MimeType, getFilename, intersection, toNonNullableArray } from '@nx-superprep/utils'
import { Choice, ChoiceType } from './choice.model'
import {
  Gameplay,
  GameplayName,
  inferGameplayInfo,
  GameplayWithTranslation,
  GameplaysWithAdditionalAudioQuestion,
  GameplaysWithAdditionalTextQuestion,
  GameplaysWithAudioQuestion,
  GameplaysWithImageQuestion,
  GameplaysWithTextQuestion,
  GameplaysWithVideoQuestion,
} from './gameplay-info.model'
import { Resource } from './resource.model'
import { Reference, ReferenceName } from './reference.model'
import { FrontendCardTemplate } from './card-template.model'
import { cloneDeep } from 'lodash-es'
import { CardTemplate } from '../../back-end'

// Helper function to create a wrong choice
const _createWrongChoice = (name: string, mime: MimeType): unknown => ({
  type: ChoiceType.wrong,
  name,
  resources: [{ key: name, name, mimeType: mime }],
  mimeType: mime,
});

/**
 * Returns a list of wrong choices, generating resources for each choice.
 * @param choices - List of choices.
 * @param answer - Correct answer.
 * @param mime - MIME type of the choices.
 */
const getWrongChoices = (choices: string[], answer: string, mime: MimeType) => choices
  .filter((c) => defined(c) && c !== answer)
  .map((c) => _createWrongChoice(c, mime))

/**
 * Extracts an element from a property.
 * @param property - Property to extract element from.
 */
const _extractElement = (property: string | string[] | undefined) => {
  switch (property?.constructor) {
    case String: {
      const value = defined(property)
      const element = value ? value.replace(/.*<\w>(\w+)<\/\w>.*/g, '$1') : undefined
      return element && property.length > element.length && element.length < 3 ? element : undefined
    }
    default:
      return undefined
  }
}

/**
 * Determines if translation should be shown for a choice.
 * @param choice - Partial resource representing the choice.
 * @param gameplayName - Name of the gameplay.
 */
const _shouldShowTranslation = (choice: Partial<Resource>, gameplayName: GameplayName) => {
  const isText = choice.mimeType === MimeType.text && choice.key !== 'element'
  const isImage = choice.mimeType === MimeType.image && choice.key !== 'element'
  return (isImage || isText) && GameplayWithTranslation.includes(gameplayName)
}

/**
 * Creates choices from the backend model.
 * @param options - Backend card template options.
 */
export const cardTemplateChoices = (options: CardTemplate): Choice[] => {
   // Adapt card question properties from backend to local model
  const cardQuestion = {
    image: options.cardQuestion.image ?? intersection(options.cardQuestion.imageChoices, options.cardAnswer.answerTexts)[0] ?? options.diagram?.fileName,
    video: options.cardQuestion.video,

    questionText: options.cardQuestion.questionText,
    questionTop: options.cardQuestion.questionTop,
    questionBottom: options.cardQuestion.questionBottom,
    endDisplayText: options.cardQuestion.endDisplayText,
    translation: options.cardQuestion.translation,

    questionAudios: options.cardQuestion.questionAudios,
    postAnswerAudios: options.cardQuestion.postAnswerAudios,

    reference: [] as Reference[],

    choices: options.cardQuestion.imageChoices?.length > 0
    ? options.cardQuestion.imageChoices
    : options.cardQuestion.audioChoices?.length > 0
      ? options.cardQuestion.audioChoices
      : options.cardQuestion.textChoices ?? [],
  }

  if ((options.cardQuestion.pronunciation ?? []).length > 0) {
    cardQuestion.reference.push(new Reference({
      referenceName: ReferenceName.pronunciation,
      referenceValueArray: [options.cardQuestion.pronunciation ?? ''],
    }))
  }

  const cardAnswer = { answerText: (options.gameplayInfo.name === GameplayName.FillInTheBlanksLetter001) ? options.cardAnswer?.answerTexts[0].split('').join('|') : options.cardAnswer.answerTexts?.join('|') }

  if (!cardQuestion || !cardAnswer) {
    log.warn('no choices in', options)
    return []
  }

  // Check for `number` gameplay with unset name
  const gameplayInfo = (!options.gameplayInfo?.name && (options.cardAnswer?.answerTexts ?? []).map((c: unknown) => Number(c)).filter((c: number) => !isNaN(c)).length > 0)
  ? new Gameplay({name: GameplayName.TextWithNumbers001})
  : new Gameplay(options.gameplayInfo ?? inferGameplayInfo(options.gameplayInfo, cardQuestion))

  const questionAudios = defined(cardQuestion.questionAudios)
  const postAnswerAudios = defined(cardQuestion.postAnswerAudios)
  const answer = defined(cardAnswer.answerText)??''
  let image = getFilename(defined(cardQuestion.image)??'')
  let element = _extractElement(cardQuestion.questionBottom)
    ?? _extractElement(cardQuestion.endDisplayText)
    ?? _extractElement(cardAnswer.answerText)

  const questionTexts = toNonNullableArray([cardQuestion.questionText, cardQuestion.questionBottom, gameplayInfo.name === GameplayName.TextWithElements001 ? element : undefined])

  let question: string | undefined = defined(questionTexts.find(e => (defined(e)??'').length > 0))
  let mime: MimeType
  let resources: { key: string; name?: string; mimeType?: string; }[] = []
  const choices: unknown[] = []

  // Generate local model entity
  const gameplayName = gameplayInfo.name
  switch (gameplayName) {
    case GameplayName.Audio001:
    case GameplayName.Audio002:
      mime = GameplaysWithTextQuestion.includes(gameplayName) ? MimeType.text : MimeType.audio
      // question as a choice
      resources = []
      question = mime === MimeType.audio ? question : undefined
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (questionAudios) {
        resources = [{ key: 'audio', name: questionAudios, mimeType: MimeType.audio }]
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: MimeType.audio })
      // answer as a choice
      resources = []
      element = _extractElement(cardQuestion.endDisplayText)
      if (element) {
        resources.push({ key: 'element', name: element, mimeType: MimeType.text })
      }
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: mime })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, mime))
      break

    case GameplayName.TextWithElements001:
    case GameplayName.TextWithNumbers001:
    case GameplayName.Text001:
    case GameplayName.Text002:
      // question as a choice
      resources = []
      question = defined(cardQuestion.questionBottom) ?? defined(cardQuestion.questionText)
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: MimeType.text })
      // answer as a choice
      resources = []
      element = _extractElement(cardQuestion.questionBottom)
      if (element) {
        resources.push({ key: 'element', name: element, mimeType: MimeType.text })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      if (answer) {
        resources.push({ key: 'answer', name: answer, mimeType: MimeType.text })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: MimeType.text })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.text))
      break

    case GameplayName.Image001:
    case GameplayName.Image002:
      // question as a choice
      resources = []
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: MimeType.image })
      // answer as a choice
      resources = []
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      if (answer) {
        resources.push({ key: 'answer', name: answer, mimeType: MimeType.text })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: MimeType.text })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.text))
      break

    case GameplayName.Text003:
    case GameplayName.Image003:
    case GameplayName.Image004:
    case GameplayName.Image005:
    case GameplayName.Image006:
    case GameplayName.TextWithImages001:
    case GameplayName.ImageWithImages001:
    case GameplayName.ImageWithNumbers001:
      mime = GameplaysWithTextQuestion.includes(gameplayName) ? MimeType.text : MimeType.image
      // question as a choice
      resources = []
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: mime })
      mime = GameplaysWithAdditionalTextQuestion.includes(gameplayName)
        ? MimeType.text
        : GameplaysWithAdditionalAudioQuestion.includes(gameplayName)
          ? MimeType.audio
          : MimeType.image
      // answer as a choice
      resources = []
      image = (mime === MimeType.image && !image) ? getFilename(defined(cardAnswer.answerText)??'') ?? image : image
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: mime })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, mime))
      break

    case GameplayName.FourImages001:
    case GameplayName.FourImages002:
    case GameplayName.FourImages003:
    case GameplayName.FourImages004:
      mime = GameplaysWithAudioQuestion.includes(gameplayName) ? MimeType.audio : MimeType.text
      // question as a choice
      resources = []
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: mime })
      // answer as a choice
      resources = []
      image = cardQuestion.choices.find((c: string) => c === answer) ?? ''
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.image })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: MimeType.image })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.image))
      break

    case GameplayName.FourTexts001:
    case GameplayName.FourTexts002:
    case GameplayName.FourTexts003:
    case GameplayName.FourTexts004:
      mime = GameplaysWithImageQuestion.includes(gameplayName)
        ? MimeType.image
        : GameplaysWithAudioQuestion.includes(gameplayName) && questionAudios
          ? MimeType.audio
          : MimeType.text
      // question as a choice
      resources = []
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: mime })
      // answer as a choice
      resources = []
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.text })
      }
      if (postAnswerAudios) {
        resources = [{ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio }]
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: MimeType.text })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.text))
      break

    case GameplayName.Diagram001:
    case GameplayName.Diagram002:
      mime = MimeType.svg
      resources = []
      resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.svg })
      }
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: mime })
      // answer as a choice
      resources = []
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.svg })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: mime })
      // wrong choices as placeholders until svg loaded
      resources = []
      for (let i = gameplayInfo.choiceCount; i > 1; i--) {
        choices.push({ type: ChoiceType.wrong, name: i.toString(), resources, mimeType: MimeType.svg })
      }
      break

    case GameplayName.TextWithPeriodicTable001:
    case GameplayName.TextWithPeriodicTable002:
    case GameplayName.ComposeWithLanguageNumbers:
    case GameplayName.MultipleChoices:
    case GameplayName.FillInWithPeriodicTable001:
    case GameplayName.FillInWithNumber001:
    case GameplayName.FillInWithLanguageNumber001:
    case GameplayName.FillTheBlanks001:
    case GameplayName.FillInTheBlanksLetter001:
      mime = GameplaysWithAudioQuestion.includes(gameplayName) ? MimeType.audio : MimeType.text
      resources = []
      resources.push({ key: 'question', name: question, mimeType: mime })
      if (questionAudios) {
        resources.push({ key: 'audio', name: questionAudios, mimeType: MimeType.audio })
      }
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.svg })
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: mime })
      // answer as a choice
      resources = []
      if (image) {
        resources.push({ key: 'image', name: image, mimeType: MimeType.svg })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: mime, isSelection: true })
      // wrong choices
      if (gameplayInfo.isMultipleChoice) {
        choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.text))
      } else {
        resources = []
        for (let i = gameplayInfo.choiceCount; i > 1; i--) {
          choices.push({ type: ChoiceType.wrong, name: i.toString(), resources, mimeType: mime })
        }
      }
      break

    case GameplayName.Video001:
    case GameplayName.Video002:
    case GameplayName.FourVideos001:
    case GameplayName.FourVideos002:
      mime = GameplaysWithVideoQuestion.includes(gameplayName)
        ? MimeType.video
        : GameplaysWithAudioQuestion.includes(gameplayName)
          ? MimeType.audio
          : MimeType.text
      image = (mime === MimeType.video)
        ? getFilename(defined(cardQuestion.video)??'') ?? getFilename(defined(cardAnswer.answerText[0])??'')
        : image
      resources = []
      if (question) {
        resources.push({ key: 'question', name: question, mimeType: MimeType.text })
      }
      if (image) {
        resources.push({ key: 'video', name: image, mimeType: MimeType.video })
      }
      if (questionAudios) {
        resources = [{ key: 'audio', name: questionAudios, mimeType: MimeType.audio }]
      }
      choices.push({ type: ChoiceType.question, name: question, resources, mimeType: MimeType.video })
      // answer as a choice
      resources = []
      image = (mime === MimeType.video) ? image ?? getFilename(defined(cardQuestion.choices.find((c: string | undefined) => c === answer))??'') : image
      if (image) {
        resources.push({ key: 'video', name: image, mimeType: MimeType.video })
      }
      if (postAnswerAudios) {
        resources.push({ key: 'audio', name: postAnswerAudios, mimeType: MimeType.audio })
      }
      if (answer) {
        resources.push({ key: 'answer', name: answer, mimeType: MimeType.text })
      }
      choices.push({ type: ChoiceType.answer, name: answer, resources, mimeType: MimeType.text })
      // wrong choices
      choices.push(...getWrongChoices(cardQuestion.choices, answer, MimeType.text))
      break

    case GameplayName.None:
    default:
      log.warn('unkown gameplay in card ' + options.id, gameplayName, options)
  }

  // Provide additional properties for question
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const questionChoice: any = choices.find((c) => (c as Choice).type === ChoiceType.question)
  if (questionChoice) {
    questionChoice.topText = defined(cardQuestion.questionTop)
    questionChoice.bottomText = defined(cardQuestion.questionBottom)
    questionChoice.endText = defined(cardQuestion.endDisplayText)
      ?? (gameplayInfo.shouldHaveEndText ? defined(cardQuestion.questionText) : undefined)
    questionChoice.translation = defined(cardQuestion.translation)
    questionChoice.shouldShowTranslation = _shouldShowTranslation(questionChoice, gameplayName)
    questionChoice.postAnswerAudios = defined(cardQuestion.postAnswerAudios)
    questionChoice.reference = {}
    cardQuestion.reference?.forEach(
      (r: { referenceName: string | number; referenceValueArray: string[]; }) => (
        questionChoice.reference[r.referenceName] = r.referenceValueArray
          .map((v) => defined(v))
          .filter((v) => Boolean(v))
        )
    )
  } else {
    if (gameplayInfo.shouldHaveQuestionChoice) { log.warn('no valid question in card ' + options.id, cardQuestion); }
  }

  return choices as Choice[]
}

export function randomizeTemplateChoices(template: FrontendCardTemplate): CardTemplate {
  const cloned = cloneDeep(template);

  const randomizeChoiceArray = (choices: string[], answers: string[], count: number): string[] => {
    log.debug(`Randomizing choices: ${choices.length} -> ${count}, keeping answer: ${answers}`);
    if (count <= 0 || choices.length === 0) return [];
    if (choices.length <= count) return shuffleArray([...choices]);

    // Ensure answers are included
    const result = [...answers];
    const remaining = choices.filter(c => !answers.includes(c));

    // Add random choices to reach count
    const needed = count - result.length;
    if (needed > 0) {
      const shuffled = shuffleArray([...remaining]);
      result.push(...shuffled.slice(0, needed));
    }

    return shuffleArray(result);
  };

  if (cloned.cardQuestion.audioChoices?.length > 0) {
    cloned.cardQuestion.audioChoices = randomizeChoiceArray(
      cloned.cardQuestion.audioChoices,
      cloned.cardAnswer.answerTexts,
      cloned.gameplayInfo.choiceCount
    );
  }

  if (cloned.cardQuestion.imageChoices?.length > 0) {
    cloned.cardQuestion.imageChoices = randomizeChoiceArray(
      cloned.cardQuestion.imageChoices,
      cloned.cardAnswer.answerTexts,
      cloned.gameplayInfo.choiceCount
    );
  }

  if (cloned.cardQuestion.textChoices?.length > 0) {
    cloned.cardQuestion.textChoices = randomizeChoiceArray(
      cloned.cardQuestion.textChoices,
      cloned.cardAnswer.answerTexts,
      cloned.gameplayInfo.choiceCount
    );
  }

  return cloned;
}

// Improved shuffleArray with proper return type
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
